/**
 * Cliente da aba Cenas.
 *
 * <p>Todas as rotas ficam sob `/mundo/cenas`, e nenhuma delas escreve em outra coleção:
 * a aba lê o mundo pelos endpoints que já existem (`mundoApi.listar`) e escreve apenas
 * as suas próprias cenas.
 */
import { api } from '../../../api/cliente';
import type { Cena, PaginaDeCenas } from './cenasTipos';

const BASE = '/api/projetos/mistik-island/mundo/cenas';

/**
 * A página inteira numa chamada: geografia, a árvore derivada do conteúdo e os números.
 *
 * <p>A rota de candidatos sumiu daqui junto com a ideia: a árvore já traz o conteúdo
 * escrito e o não escrito, então não há uma segunda lista para buscar ao abrir a área.
 */
export function pagina(): Promise<PaginaDeCenas> {
  return api<PaginaDeCenas>(BASE);
}

/**
 * A ordem de um nível inteiro, como o arrastar a deixou.
 *
 * <p>Manda o nível todo, e não "mova este para N": a tela já sabe o resultado, e o
 * resultado é idempotente — repetir o mesmo pedido dá o mesmo desenho, o que um
 * deslocamento relativo nunca garante quando duas gravações se cruzam.
 */
export function reordenar(
  areaId: number,
  nos: { tipo: string; alvoId: number; nome: string }[]
): Promise<PaginaDeCenas> {
  return api<PaginaDeCenas>(`${BASE}/ordem`, {
    method: 'PATCH',
    body: JSON.stringify({ areaId, nos }),
  });
}

export function carregar(id: number): Promise<Cena> {
  return api<Cena>(`${BASE}/${id}`);
}

/** Criar pede só o nome; área e cena mãe vêm dos atalhos que já sabem onde estão. */
export function criar(corpo: Record<string, unknown>): Promise<Cena> {
  return api<Cena>(BASE, { method: 'POST', body: JSON.stringify(corpo) });
}

export function salvar(id: number, mudancas: Record<string, unknown>): Promise<Cena> {
  return api<Cena>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });
}

/**
 * Troca de lugar com a irmã de cima ou de baixo. A permuta é do servidor: se fossem
 * dois PATCH daqui, uma falha entre eles deixaria duas cenas no mesmo lugar.
 */
export function mover(id: number, direcao: 'cima' | 'baixo'): Promise<Cena> {
  return api<Cena>(`${BASE}/${id}/ordem`, {
    method: 'PATCH',
    body: JSON.stringify({ direcao }),
  });
}

/** Sem {@code confirmado}, a API responde 409 quando há filhas ou escolhas apontando. */
export function excluir(id: number, confirmado = false): Promise<void> {
  return api<void>(`${BASE}/${id}?confirmar=${confirmado}`, { method: 'DELETE' });
}
